import { useCallback, useRef, useState } from 'react';
import { logger } from '@garnish/logger';
import StringCacheMap from 'string-cache-map';
import { fetchRadarPredictions,
  RADAR_LAYERS,
  type RadarAddress,
  type RadarPredictions,
  useDebounceFn
} from '@sg/garnish';

import { type MinimumAddress } from '../../locations.types';

// ─────────────────────────────────────────────────────────────────────────────

// @DEV: Doing this inline on prop destructure causes a re-render issue. Needs a stable reference.
const DEFAULT_LAYERS = [RADAR_LAYERS.ADDRESS];

export const useAddressAutocomplete = (
  params: UseAddressAutocompleteParams,
) => {
  const {
    apiKey,
    minChars = 3,
    layers = DEFAULT_LAYERS,
    near,
    country,
  } = params;

  // ───── state ───────────────────────────────────────────────
  const [value, setValue] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [networkError, setNetworkError] = useState<unknown>();
  const [predictions, setPredictions] = useState<RadarPredictions>([]);

  // ───── cache ───────────────────────────────────────────────
  const predictionsCache = useRef(new StringCacheMap(10));

  // ───── radar predictions ───────────────────────────────────
  const getPredictions = useCallback(
    async (query = '') => {
      const validInput = query.trim().length >= minChars;
      let fetchedPredictions: RadarPredictions = [];

      try {
        if (validInput) {
          const response = await fetchRadarPredictions({
            query,
            apiKey,
            layers,
            near,
            country,
          });

          fetchedPredictions = response.addresses;

          predictionsCache.current.set(query, {
            predictions: fetchedPredictions,
          });
        }
      } catch (error: unknown) {
        logger.error('[useAddressAutocomplete]', error);
        setNetworkError(error as Error);
      } finally {
        setPredictions(fetchedPredictions);
        setLoading(false);
      }
    },
    [apiKey, minChars, layers, near, country],
  );

  const getPredictionsDebounced = useDebounceFn(getPredictions);

  const setSearchString = useCallback(
    (query: string) => {
      const isSearchable = query.length >= minChars;
      const cachedPredictions =
        predictionsCache.current.get<PredictionsCacheItem>(query);

      setValue(query);
      setNetworkError(undefined);

      if (cachedPredictions) {
        setPredictions(cachedPredictions.predictions);

        return;
      }

      setLoading(isSearchable);

      if (isSearchable) getPredictionsDebounced(query);
    },
    [getPredictionsDebounced, minChars],
  );

  // ───── reset results ───────────────────────────────────────

  const clearAutoCompleteResults = useCallback(() => {
    setValue(undefined);
    setLoading(false);
    setNetworkError(undefined);
    setPredictions([]);
  }, []);

  // ───────────────────────────────────────────────────────────

  const validInput = (value ?? '').length >= minChars;
  const noResults = !loading && validInput && predictions.length === 0;

  return {
    searchString: value ?? '',
    predictions,
    loading,
    error: networkError,
    noResults,
    setSearchString,
    clearAutoCompleteResults,
  } as const;
};

// ─── Utilities ───────────────────────────────────────────────────────────────

export const convertRadarAddress = (address: RadarAddress): MinimumAddress => ({
  latitude: address.latitude,
  longitude: address.longitude,
  street: `${address.number} ${address.street}`,
  zipCode: address.postalCode,
  city: address.city,
  state: address.stateCode,
  country: address.country,
  formattedAddress: address.formattedAddress,
});

export const formatRadarAddress = (
  address: Partial<MinimumAddress> & Readonly<{ name?: string | null }>,
  withName?: boolean,
  streetOnly?: boolean,
): string => {
  const {
    name = '',
    street = '',
    city = '',
    state = '',
    zipCode = '',
  } = address;

  const stateAndZipCode = `${state} ${zipCode}`.trim();
  const addressPartials = streetOnly
    ? [street]
    : [street, city, stateAndZipCode];
  const formattedAddress = addressPartials.filter(Boolean).join(', ');
  const shouldAddName = Boolean(withName && name);

  if (!formattedAddress) return '';

  return shouldAddName ? `${name} · ${formattedAddress}` : formattedAddress;
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseAddressAutocompleteParams = {
  apiKey: string;
  minChars?: number;
  layers?: string[];
  near?: string;
  country?: string;
};

type PredictionsCacheItem = {
  predictions: RadarPredictions;
};
